import { useMemo } from 'react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useGroup } from '../../contexts/GroupContext';
import { plural } from '../../nucleus/format';

// ── identities ─────────────────────────────────────────────────────────────
// ONE NAME FIELD (owner's call): the name is written to both
// the node's title and value_json.display_name, so they can never differ.
// First and last name are a person's only. A group's identities are nodes the
// group owns, and only its admins change them.

export const nameOf = (node) => node?.value_json?.display_name || node?.title || 'Untitled';

export const IDENTITY_FIELDS = [
    { key: 'first_name', label: 'First name', personalOnly: true },
    { key: 'last_name', label: 'Last name', personalOnly: true },
];

/** What "my identities" means in the space you are in: whose they are, who may
 *  edit them, which is the default, and the calls that reach them. */
export function useIdentityScope() {
    const { activeGroup, activeGroupId, refreshGroups } = useGroup();
    const { user, refreshUser } = useAuth();
    const groupId = activeGroupId || null;
    const role = activeGroup?.my_role || activeGroup?.role;
    const groupName = activeGroup?.name;
    const groupDefault = activeGroup?.default_profile_node_id || null;
    const userId = user?.id;
    const userDefault = user?.default_profile_node_id || null;
    const username = user?.username;

    return useMemo(() => (groupId ? {
        kind: 'group',
        key: `group:${groupId}`,
        canEdit: role === 'admin',
        defaultId: groupDefault,
        fallbackName: groupName,
        denied: `Only an admin of ${groupName || 'this group'} can change these`,
        list: () => api.getGroupNodes(groupId, { schema_name: 'profile', limit: 50 }),
        usage: () => api.request(`/identities/usage?group_id=${groupId}`),
        create: (body) => api.createGroupNode(groupId, { ...body, schema_name: 'profile' }),
        update: (id, body) => api.updateGroupNode(groupId, id, body),
        clearPhoto: (id) => api.request(`/groups/${groupId}/nodes/${id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json-patch+json' },
            body: [{ op: 'remove', path: '/value_json/avatar_file_id' }],
        }),
        remove: (id) => api.deleteGroupNode(groupId, id),
        upload: (file) => api.uploadGroupFile(groupId, file),
        setDefault: (id) => api.updateGroup(groupId, { default_profile_node_id: id }),
        refresh: refreshGroups,
    } : {
        kind: 'personal',
        key: `user:${userId || 'none'}`,
        canEdit: true,
        defaultId: userDefault,
        fallbackName: username,
        list: () => api.getNodes({ schema_id: 'profile', limit: 50 }),
        usage: () => api.request('/identities/usage'),
        // BY NAME, never by a looked-up id: GET /schemas has no
        // schema_name filter, so a lookup wrote identities onto the wrong schema.
        create: (body) => api.request('/nodes', {
            method: 'POST',
            body: { title: body.title, value_json: body.value_json, schema_name: 'profile' },
        }),
        update: (id, body) => api.updateNode(id, body),
        // Clearing is a PATCH: PUT merges, so a key left out keeps its old value.
        clearPhoto: (id) => api.patchNode(id, [{ op: 'remove', path: '/value_json/avatar_file_id' }]),
        remove: (id) => api.deleteNode(id),
        upload: (file) => api.uploadFileAsNode(file),
        setDefault: (id) => api.updateUser(userId, { default_profile_node_id: id }),
        refresh: refreshUser,
    }), [groupId, role, groupDefault, groupName, userId, userDefault, username, refreshGroups, refreshUser]);
}

// A photo's signed URL (they expire, so they are read fresh each load).
export async function photoUrlOf(fileId) {
    if (!fileId) return null;
    const dl = await api.getNodeDownloadUrl(fileId).catch(() => null);
    return dl?.download_url || null;
}

// ── security ───────────────────────────────────────────────────────────────

// How long you may be away before signing in again (an idle timeout: using
// ofSelf restarts the clock). Unset is the refresh lifetime, 30 days.
export const SESSION_PRESETS = [
    { value: '3600', label: '1 hour' },
    { value: '14400', label: '4 hours' },
    { value: '28800', label: '8 hours' },
    { value: '86400', label: '24 hours' },
    { value: '604800', label: '7 days' },
    { value: '', label: '30 days' },
];

export function formatDuration(s) {
    if (!s) return '30 days (the default)';
    if (s >= 86400) return plural(Math.round(s / 86400), 'day');
    if (s >= 3600) return plural(Math.round(s / 3600), 'hour');
    return `${Math.round(s / 60)} min`;
}

export const DETECTED_TZ = (() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch { return null; }
})();

export const KNOWN_ZONES = (() => {
    try { return Intl.supportedValuesOf('timeZone'); } catch { return DETECTED_TZ ? [DETECTED_TZ] : []; }
})();

// Where a session stands, in words.
export function sessionName(s, appName) {
    if (s.app?.name) return s.app.name;
    if (s.app_id) return appName(s.app_id) || 'An app';
    if (s.audience === 'nucleus-mac') return 'Nucleus for Mac';
    // The app's name is COSM's word for it, not one Paradigm checked: say whose it is.
    if (s.delegated) return `${s.delegated.label || 'An app'} (as named by COSM)`;
    if (s.audience === 'builder') return 'dev.oS and the paradigm CLI';
    return 'Nucleus on the web';
}

export function deviceOf(ua = '') {
    if (!ua) return null;
    const os = /Mac OS X|Macintosh/.test(ua) ? 'macOS' : /Windows/.test(ua) ? 'Windows' : /iPhone|iPad/.test(ua) ? 'iOS'
        : /Android/.test(ua) ? 'Android' : /Linux/.test(ua) ? 'Linux' : null;
    const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox'
        : /Safari\//.test(ua) ? 'Safari' : null;
    return [browser, os].filter(Boolean).join(' on ') || null;
}

// ── the consent page, asked from Self, comes back to Self ──────────────────
export const backTo = (path) => encodeURIComponent(path);

